// src/pages/VoiceAndSIPSetup.js
// Voice & SIP setup wizard for tenant telephony preferences.

import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  FileText,
  Loader2,
  PhoneForwarded,
  RadioTower
} from 'lucide-react';
import * as Sentry from '@sentry/browser';
import { useEffect, useMemo, useState } from 'react';
import SIPInstructions from '../components/telephony/SIPInstructions';
import { useAuth } from '../hooks/useAuth';
import { TelephonyProfile } from '../models/TelephonyProfile';
import {
  getTelephonyProfile,
  setTelephonyProfile
} from '../services/data/telephonyProfile';
import { applyTelephonyProfile, pingService } from '../services/TelephonyApi';
import InfoTooltip from '../components/common/InfoTooltip';

const NUMBER_TYPE_OPTIONS = [
  {
    value: 'business',
    label: 'Business number (PBX/VoIP capable)',
    description: 'Recommended for SIP trunking integration.'
  },
  {
    value: 'personal',
    label: 'Personal mobile number',
    description: 'SIP trunking not supported; we’ll guide you with call forwarding instead.'
  }
];

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States (+1)' },
  { value: 'CA', label: 'Canada (+1)' }
];

const PBX_VENDORS = ['Cisco', 'Mitel', '3CX', 'FreePBX', 'Avaya', 'Zoom Phone', 'Other'];
const CARRIERS = ['Rogers', 'Bell', 'Telus', 'Shaw', 'AT&T', 'Verizon', 'T-Mobile', 'Other'];
const ROUTING_POLICIES = [
  { value: 'all', label: 'Route all calls to the AI agent' },
  { value: 'after_hours', label: 'Route after-hours calls' },
  { value: 'overflow', label: 'Route overflow or backup calls' }
];

const SIP_STATUS_LABELS = {
  not_started: 'Not started',
  collecting_info: 'Collecting info',
  pending_it: 'Pending IT',
  ready: 'Ready',
  live: 'Live'
};

const FORWARDING_STATUS_LABELS = {
  not_applicable: 'Not applicable',
  not_started: 'Not started',
  ready: 'Ready',
  live: 'Live'
};

const STATUS_STYLES = {
  default: 'border-gray-200 bg-gray-100 text-gray-700',
  info: 'border-blue-200 bg-blue-100 text-blue-700',
  success: 'border-green-200 bg-green-100 text-green-700',
  warning: 'border-amber-200 bg-amber-100 text-amber-700'
};

const isValidE164 = (value) => /^\+[1-9]\d{6,14}$/.test(value);

const trackEvent = (eventName, payload) => {
  if (typeof window !== 'undefined' && window?.analytics?.track) {
    window.analytics.track(eventName, payload);
  } else {
    console.debug(`[telemetry] ${eventName}`, payload);
  }
};

const getStatusClassName = (status, type = 'sip') => {
  if (type === 'sip') {
    switch (status) {
      case 'pending_it':
        return STATUS_STYLES.info;
      case 'ready':
      case 'live':
        return STATUS_STYLES.success;
      case 'collecting_info':
        return STATUS_STYLES.warning;
      default:
        return STATUS_STYLES.default;
    }
  }

  switch (status) {
    case 'ready':
    case 'live':
      return STATUS_STYLES.success;
    case 'not_started':
      return STATUS_STYLES.warning;
    default:
      return STATUS_STYLES.default;
  }
};

const VoiceAndSIPSetup = () => {
  const { user } = useAuth();
  const tenantId = user?.businessId;

  const [profile, setProfile] = useState(TelephonyProfile.createDefault());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pingOk, setPingOk] = useState(null);
  const [applying, setApplying] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const tooltipText =
    pingOk == null
      ? 'We’re checking connectivity to the Cloud Run telephony service. This confirms your dashboard can reach the backend.'
      : pingOk
      ? 'Connected to the telephony backend. Cloud Run function is reachable and responding. You can proceed with Twilio setup steps.'
      : 'Not reachable. Common causes: CORS origin mismatch, missing Authorization header (if auth is required), disabled public access, wrong base URL, or the service is down. Check Network tab, function logs, and Sentry.';

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await getTelephonyProfile(tenantId);
        const model = data ? new TelephonyProfile(data) : TelephonyProfile.createDefault();
        if (!isMounted) return;

        setProfile(model);
        setPhoneInput(model.phoneNumberE164 || '');
        setError(null);
      } catch (err) {
        console.error('Failed to load telephony profile', err);
        Sentry.withScope((scope) => {
          scope.setLevel('error');
          scope.setTag('feature', 'voice-sip-setup');
          scope.setTag('businessId', tenantId || 'unknown');
          scope.setFingerprint(['telephony-profile-read', tenantId || 'unknown']);
          Sentry.captureException(err);
        });
        if (isMounted) {
          setError('Unable to load telephony preferences. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const resp = await pingService();
        if (!alive) return;
        setPingOk(resp?.pong === true);
      } catch {
        if (!alive) return;
        setPingOk(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    setPhoneInput(profile.phoneNumberE164 || '');
  }, [profile.phoneNumberE164]);

  const applyUpdates = async (updates, telemetryEvent = null) => {
    if (!tenantId) {
      return;
    }

    const previousSnapshot = new TelephonyProfile(profile.toFirestore());
    const optimistic = profile.merge(updates);
    const payload = optimistic.toFirestore();

    setProfile(optimistic);
    setSaving(true);

    Sentry.addBreadcrumb({
      category: 'telephony',
      message: 'User updated telephony profile',
      level: 'info',
      data: {
        businessId: tenantId || 'unknown',
        fields: Object.keys(updates || {})
      }
    });

    try {
      await setTelephonyProfile(tenantId, payload);
      setError(null);

      if (telemetryEvent) {
        trackEvent(telemetryEvent, {
          tenantId,
          updates
        });
      }
    } catch (err) {
      console.error('Failed to update telephony profile', err);
      setProfile(previousSnapshot);
      setError('Could not save your changes. Please try again.');
      Sentry.withScope((scope) => {
        scope.setLevel('error');
        scope.setTag('feature', 'voice-sip-setup');
        scope.setTag('businessId', tenantId);
        scope.setFingerprint(['telephony-profile-write', tenantId || 'unknown']);
        Sentry.captureException(err);
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNumberTypeChange = (value) => {
    const nextCountry = profile.countryIso2 || 'US';
    const updates = {
      numberType: value,
      countryIso2: nextCountry,
      sip: {
        desired: value === 'business'
      },
      forwarding: {
        desired: value === 'personal'
      }
    };

    if (value === 'personal' && profile.forwarding.status === 'not_applicable') {
      updates.forwarding.status = 'not_started';
    }

    if (value === 'business' && profile.sip.status === 'not_started') {
      updates.sip.status = 'collecting_info';
    }

    applyUpdates(updates, 'telephony.numberType.selected');
  };

  const handlePhoneChange = (value) => {
    setPhoneInput(value);

    if (!value) {
      setPhoneError(null);
      if (profile.phoneNumberE164) {
        applyUpdates({ phoneNumberE164: null });
      }
      return;
    }

    if (isValidE164(value)) {
      setPhoneError(null);
      if (value !== profile.phoneNumberE164) {
        applyUpdates({ phoneNumberE164: value });
      }
    } else {
      setPhoneError('Enter phone in E.164 format (e.g. +14165551234).');
    }
  };

  const handleCountryChange = (value) => {
    applyUpdates({ countryIso2: value });
  };

  const handleSipField = (field, value) => {
    applyUpdates({ sip: { [field]: value } });
  };

  const handleSipStatus = (status) => {
    applyUpdates(
      {
        sip: {
          status,
          desired: true
        }
      },
      'telephony.sip.status.updated'
    );
  };

  const handleForwardingStatus = (status) => {
    applyUpdates(
      {
        forwarding: {
          status,
          desired: true
        }
      },
      'telephony.forwarding.status.updated'
    );
  };

  const instructionDocuments = useMemo(() => {
    if (profile.isBusinessNumber()) {
      const docs = ['sip_overview', 'sip_steps_generic'];
      if (profile.requiresRogersSIPNote()) {
        docs.push('sip_steps_rogers');
      }
      docs.push('troubleshooting');
      return docs;
    }

    return ['forwarding_personal_mobile', 'troubleshooting'];
  }, [profile.numberType, profile.countryIso2, profile.sip.carrier]);

  const showRogersNote = profile.isBusinessNumber() && profile.requiresRogersSIPNote();

  const handleSaveAndApply = async () => {
    if (!tenantId) {
      window.alert('No business selected. Please sign in again.');
      return;
    }

    if (applying) {
      return;
    }

    if (profile.isBusinessNumber()) {
      if (!profile.sip.pbxVendor || !profile.sip.carrier || !profile.sip.routingPolicy) {
        window.alert('Please fill all PBX, carrier, and routing fields before applying.');
        return;
      }
    }

    const snapshot = profile.toFirestore();

    setApplying(true);

    try {
      await setTelephonyProfile(tenantId, snapshot);

      const result = await applyTelephonyProfile({
        businessId: tenantId,
        profile: snapshot
      });

      Sentry.addBreadcrumb({
        category: 'telephony.apply',
        level: 'info',
        data: { ok: true, result },
        message: 'Telephony profile apply triggered'
      });

      window.alert('Telephony settings saved and backend tasks triggered.');
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: 'voice-sip-setup', endpoint: 'apply_profile' }
      });
      window.alert(err?.message || 'Failed to save/apply telephony settings.');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-700 flex items-start">
          <AlertCircle className="h-6 w-6 mr-3 mt-1" />
          <div>
            <h2 className="text-lg font-semibold">Business account required</h2>
            <p className="mt-2">
              You need an active business account to configure Voice &amp; SIP preferences.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Voice &amp; SIP Setup</h1>
        <p className="mt-2 text-gray-600">
          Configure how your business routes calls to the AI assistant through SIP trunking or call
          forwarding.
        </p>
      </div>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            padding: '4px 8px',
            borderRadius: 8,
            fontSize: 12,
            background: pingOk == null ? '#eee' : pingOk ? '#d4f8d4' : '#fde2e2',
            color: '#333'
          }}
          title="Telephony service connectivity"
        >
          {pingOk == null
            ? 'Telephony service: checking…'
            : pingOk
            ? 'Telephony service: reachable'
            : 'Telephony service: unreachable'}
        </span>
        <InfoTooltip text={tooltipText} ariaLabel="Telephony service status details" />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 flex items-start">
          <AlertCircle className="h-5 w-5 mr-3 mt-1" />
          <div>
            <p className="font-medium">We hit a snag saving your changes.</p>
            <p>Please retry. If this keeps happening contact support.</p>
          </div>
        </div>
      )}

      {saving && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-700 flex items-center">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Saving your changes...
        </div>
      )}

      {/* Panel A */}
      <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <header className="border-b border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">Choose Your Number Type</h2>
          <p className="text-gray-600">
            Tell us what kind of number you’re routing so we can recommend the right integration path.
          </p>
        </header>
        <div className="p-6 space-y-6">
          <div className="grid gap-4">
            {NUMBER_TYPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  profile.numberType === option.value
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    name="number-type"
                    value={option.value}
                    checked={profile.numberType === option.value}
                    onChange={() => handleNumberTypeChange(option.value)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{option.label}</p>
                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                  </div>
                </div>
              </label>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary business phone (E.164)
              </label>
              <input
                type="tel"
                value={phoneInput}
                onChange={(event) => handlePhoneChange(event.target.value)}
                placeholder="+14165551234"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                aria-invalid={Boolean(phoneError)}
                aria-describedby="phone-error"
              />
              {phoneError && (
                <p id="phone-error" className="mt-2 text-sm text-red-600">
                  {phoneError}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number country</label>
              <select
                value={profile.countryIso2 || ''}
                onChange={(event) => handleCountryChange(event.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>
                  Select country
                </option>
                {COUNTRY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Panel B */}
      <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <header className="border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recommended Path</h2>
            <p className="text-gray-600">
              We tailor the workflow based on your number type so you can go live faster.
            </p>
          </div>
          <div
            className={`px-3 py-1 rounded-full border text-sm font-medium ${getStatusClassName(
              profile.isBusinessNumber()
                ? profile.sip.status
                : profile.forwarding.status,
              profile.isBusinessNumber() ? 'sip' : 'forwarding'
            )}`}
          >
            {profile.isBusinessNumber()
              ? SIP_STATUS_LABELS[profile.sip.status] || 'Not started'
              : FORWARDING_STATUS_LABELS[profile.forwarding.status] || 'Not started'}
          </div>
        </header>

        <div className="p-6 space-y-6">
          {profile.isBusinessNumber() ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-800 flex items-start">
                <RadioTower className="h-6 w-6 mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900">SIP Integration</h3>
                  <p className="text-sm mt-2">
                    We’ll collect PBX and carrier details so our team can provision the SIP trunk to
                    your Twilio domain.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PBX vendor
                  </label>
                  <select
                    value={profile.sip.pbxVendor || ''}
                    onChange={(event) => handleSipField('pbxVendor', event.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select vendor</option>
                    {PBX_VENDORS.map((vendor) => (
                      <option key={vendor} value={vendor}>
                        {vendor}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Carrier</label>
                  <select
                    value={profile.sip.carrier || ''}
                    onChange={(event) => handleSipField('carrier', event.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select carrier</option>
                    {CARRIERS.map((carrier) => (
                      <option key={carrier} value={carrier}>
                        {carrier}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Routing policy
                  </label>
                  <select
                    value={profile.sip.routingPolicy || ''}
                    onChange={(event) => handleSipField('routingPolicy', event.target.value || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose routing</option>
                    {ROUTING_POLICIES.map((policy) => (
                      <option key={policy.value} value={policy.value}>
                        {policy.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {showRogersNote && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-700 flex items-start">
                  <Info className="h-5 w-5 mr-3 mt-1" />
                  <div>
                    <p className="font-medium">Rogers SIP requires a business PBX account.</p>
                    <p className="text-sm mt-1">
                      Confirm that your PBX is registered with Rogers Business SIP before requesting
                      the trunk. Personal lines are not supported.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <ToggleInstructionsButton
                  isOpen={instructionsOpen}
                  onClick={() => setInstructionsOpen((prev) => !prev)}
                  color="blue"
                  openLabel="Hide SIP Instructions"
                  closedLabel="Open SIP Instructions"
                />

                <button
                  type="button"
                  onClick={() => handleSipStatus('pending_it')}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark as Ready for IT
                </button>

                <button
                  type="button"
                  onClick={handleSaveAndApply}
                  disabled={applying || saving}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    background: applying || saving ? '#6CA8FF' : '#007BFF',
                    color: '#fff',
                    border: 'none',
                    cursor: applying || saving ? 'not-allowed' : 'pointer',
                    opacity: applying || saving ? 0.9 : 1
                  }}
                  aria-disabled={applying || saving}
                >
                  {applying ? 'Applying...' : 'Save & Apply'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 flex items-start">
                <PhoneForwarded className="h-6 w-6 mr-3 mt-1" />
                <div>
                  <h3 className="font-semibold text-green-900">Call Forwarding Guidance</h3>
                  <p className="text-sm mt-2">
                    We’ll walk you through forwarding your personal mobile or carrier line to the AI
                    assistant.
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <ToggleInstructionsButton
                  isOpen={instructionsOpen}
                  onClick={() => setInstructionsOpen((prev) => !prev)}
                  color="green"
                  openLabel="Hide Forwarding Steps"
                  closedLabel="View Forwarding Steps"
                />

                <button
                  type="button"
                  onClick={() => handleForwardingStatus('ready')}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Mark as Ready
                </button>

                <button
                  type="button"
                  onClick={handleSaveAndApply}
                  disabled={applying || saving}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    background: applying || saving ? '#6CA8FF' : '#007BFF',
                    color: '#fff',
                    border: 'none',
                    cursor: applying || saving ? 'not-allowed' : 'pointer',
                    opacity: applying || saving ? 0.9 : 1
                  }}
                  aria-disabled={applying || saving}
                >
                  {applying ? 'Applying...' : 'Save & Apply'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Panel C */}
      <section className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
          onClick={() => setInstructionsOpen((prev) => !prev)}
        >
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-gray-500" />
            <span className="ml-3 text-lg font-semibold text-gray-900">Instructions</span>
          </div>
          {instructionsOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </button>
        {instructionsOpen && (
          <div className="border-t border-gray-200 p-6">
            <SIPInstructions documents={instructionDocuments} />
          </div>
        )}
      </section>

      <footer className="text-sm text-gray-500">
        Last updated:{' '}
        {profile.sip.lastUpdated
          ? new Date(profile.sip.lastUpdated).toLocaleString()
          : 'Not recorded'}
      </footer>
    </div>
  );
};

const ToggleInstructionsButton = ({
  isOpen,
  onClick,
  color,
  openLabel,
  closedLabel
}) => {
  const colorClasses =
    color === 'green'
      ? 'border-green-600 text-green-700 hover:bg-green-50 focus:ring-green-500'
      : 'border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${colorClasses}`}
    >
      <FileText className="h-4 w-4" />
      <span className="ml-2">{isOpen ? openLabel : closedLabel}</span>
    </button>
  );
};

export default VoiceAndSIPSetup;
